import { ClickableCard } from "@/components/property/clickable-card";
import { BookingWithOffers } from "@/components/promotions/booking-with-offers";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import type { PublicProperty } from "@shared/schema";
import { useBooking } from "@/hooks/use-booking-context";
import { RateLine } from "@/components/property/rate-line";
import { LastMinuteOffer } from "@/components/promotions/last-minute-offer";
import { canonicalPropertySlug, KUALOA_PROPERTY_SLUG } from "@shared/property-context";

/**
 * Booking in a lightbox, everywhere.
 *
 * Guests lost their place when a "Book now" or "Check dates" sent them to a
 * separate booking page. Now any link to a booking page (/properties/<slug>/book,
 * /booking, #check-availability, #book) opens this overlay on top of the page
 * they are reading instead: the calendar and the price for that home, dates
 * carried in from the link, and a "View full details" link for anyone who
 * wants the whole listing. With no home named and more than one home listed,
 * it opens on a short chooser first.
 *
 * The links stay real links (crawlable, and they still work before the page
 * has loaded or with a modifier key for a new tab); a capture-phase click
 * handler turns an ordinary click into the lightbox. Anything that is not a
 * link can call openBookingLightbox().
 */

export type BookingLightboxRequest = {
  slug?: string | null;
  start?: string | null;
  end?: string | null;
  guests?: string | null;
};

const EVENT = "booking-lightbox:open";

/** Open the booking lightbox from anywhere (buttons, popups, non-React code). */
export function openBookingLightbox(request: BookingLightboxRequest = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<BookingLightboxRequest>(EVENT, { detail: request }));
}

const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;
const coverOf = (home: PublicProperty) =>
  home.hero_image_url || [...(home.photos || [])].sort((a, b) => a.sort_order - b.sort_order)[0]?.url;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Which booking request, if any, a link's href stands for. */
export function bookingRequestFromHref(href: string, currentPath: string): BookingLightboxRequest | null {
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  const params = url.searchParams;
  const dates = {
    start: YMD.test(params.get("start") || "") ? params.get("start") : null,
    end: YMD.test(params.get("end") || "") ? params.get("end") : null,
    guests: /^\d+$/.test(params.get("guests") || "") ? params.get("guests") : null,
  };
  const pageHome = currentPath === "/stay"
    ? KUALOA_PROPERTY_SLUG
    : currentPath.match(/^\/properties\/([^/?#]+)/)?.[1] ?? null;
  const book = url.pathname.match(/^\/properties\/([^/]+)\/book\/?$/);
  if (book) return { slug: canonicalPropertySlug(decodeURIComponent(book[1])), ...dates };
  if (url.pathname === "/booking" || url.pathname === "/booking/") return { slug: pageHome, ...dates };
  // In-page anchors that used to scroll to a booking form.
  const samePage = url.pathname === window.location.pathname;
  if (samePage && (url.hash === "#check-availability" || url.hash === "#book" || url.hash === "#booking-form")) {
    return { slug: pageHome, ...dates };
  }
  return null;
}

export function BookingLightboxHost() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [calendarKey, setCalendarKey] = useState(0);
  const { setProperty, setCheckIn, setCheckOut, setGuests, updateBookingDates } = useBooking();
  const { data: homes = [] } = useQuery<PublicProperty[]>({ queryKey: ["/api/properties"], staleTime: 10 * 60 * 1000 });
  const listed = homes.filter((h) => h.slug);
  const home = slug ? listed.find((h) => h.slug === slug) ?? null : null;
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const show = useCallback((request: BookingLightboxRequest) => {
    returnFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    if (request.start) updateBookingDates(request.start, request.end || undefined);
    else if (request.end) setCheckOut(request.end);
    if (request.guests) setGuests(request.guests);
    const requestedSlug = request.slug ? canonicalPropertySlug(request.slug) : null;
    const requestedHome = listed.find((h) => h.slug === requestedSlug);
    if (requestedHome) setProperty(requestedHome.id, requestedHome.slug!);
    else if (!requestedSlug) setProperty("", "");
    setSlug(requestedSlug);
    setCalendarKey((k) => k + 1);
    setOpen(true);
  }, [updateBookingDates, setCheckOut, setGuests, listed, setProperty]);

  const close = useCallback(() => {
    setOpen(false);
    returnFocusRef.current?.focus?.();
  }, []);

  // A single home listed: skip the chooser.
  useEffect(() => {
    if (open && !slug && listed.length === 1) setSlug(listed[0].slug!);
  }, [open, slug, listed]);

  // The shared booking context books whichever home the lightbox shows, so the
  // calendar reads that home's availability and the quote that home's rates.
  useEffect(() => {
    if (open && home) setProperty(home.id, home.slug!);
  }, [open, home, setProperty]);

  // Opening requests from code (openBookingLightbox).
  useEffect(() => {
    const onOpen = (e: Event) => show((e as CustomEvent<BookingLightboxRequest>).detail || {});
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, [show]);

  // Every ordinary click on a booking link opens the lightbox instead.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const path = window.location.pathname;
      if (path.startsWith("/admin") || path.startsWith("/booking/")) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download") || anchor.dataset.noLightbox !== undefined) return;
      const request = bookingRequestFromHref(anchor.getAttribute("href") || "", path);
      if (!request) return;
      e.preventDefault();
      e.stopPropagation();
      show(request);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [show]);

  // Leaving the page (checkout, a "view more" link) closes it.
  const lastLocation = useRef(location);
  useEffect(() => {
    if (lastLocation.current !== location) {
      lastLocation.current = location;
      setOpen(false);
    }
  }, [location]);

  // Escape closes, unless the calendar on top of it is open (it closes first).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Something open on top of it (the calendar, the guest details step)
      // takes this Escape; the lightbox stays.
      if (document.querySelector('[data-testid="calendar-modal"]')) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      close();
    };
    // Capture phase, registered before anything opens on top: it runs while
    // that layer is still open, so the check above sees it.
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, close]);

  // No page scroll behind it; focus moves into it (onto its close button: the
  // site's focus ring rule repositions any focused [tabindex] element, so the
  // panel itself is never the one focused).
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const choosing = !home;
  return createPortal(
    <>
      {/* Below Radix dialogs (z-50: the guest details step opens on top of
          this) and the calendar (z-200), above the header (z-40). */}
      <div className="fixed inset-0 z-[45] bg-black/50" onClick={close} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={home ? `Book ${home.title}` : "Choose a home to book"}
        data-testid="booking-lightbox"
        className="fixed inset-x-0 bottom-0 z-[46] mx-auto overscroll-contain max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-shell shadow-2xl outline-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90dvh] sm:w-[min(94vw,560px)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-sand-deep bg-shell/95 px-5 py-3 backdrop-blur">
          {home && listed.length > 1 ? (
            <button type="button" onClick={() => setSlug(null)} className="inline-flex items-center gap-1 font-sans text-[12px] uppercase tracking-[0.16em] text-ink/70 hover:text-ink" data-testid="booking-lightbox-switch">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All homes
            </button>
          ) : (
            <span className="font-sans text-[12px] uppercase tracking-[0.16em] text-ink/70">{choosing ? "Choose a home" : "Book your stay"}</span>
          )}
          <button ref={closeRef} type="button" onClick={close} className="rounded-full p-2 text-ink hover:bg-sand" aria-label="Close" data-testid="booking-lightbox-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {choosing ? (
          <ul className="space-y-3 p-5" data-testid="booking-lightbox-chooser">
            {listed.map((h) => {
              const cover = coverOf(h);
              return (
                <li key={h.id} className="text-center">
                  <ClickableCard as="div" href={`/properties/${h.slug}`} onNavigate={close}
                    className="rounded-xl border border-sand-deep bg-white p-3 transition-colors hover:border-ink"
                    data-testid={`booking-lightbox-card-${h.slug}`}>
                    <Link href={`/properties/${h.slug}`} onClick={close} className="flex flex-col items-center gap-4" data-testid={`booking-lightbox-property-${h.slug}`}>
                      {cover && <img src={img(cover, 240)} alt="" className="h-20 w-24 shrink-0 rounded-lg object-cover" loading="lazy" />}
                      <span className="min-w-0 flex-1">
                        <span className="block font-serif text-lg text-ink">{h.title}</span>
                        <span className="block font-sans text-[12px] text-ink/70">{h.bedrooms} bedrooms · up to {h.max_guests} guests</span>
                        <RateLine home={h} size="sm" className="mt-1" />
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-ink/60" aria-hidden="true" />
                    </Link>
                    <div className="mt-3 flex flex-wrap justify-center gap-2">
                      <Link href={`/properties/${h.slug}`} onClick={close} aria-label={`View Property: ${h.title}`}
                        className="inline-flex min-h-11 items-center justify-center rounded-lg border border-ink bg-ink px-4 font-sans text-sm text-shell hover:bg-ink/90">View Property</Link>
                      <button type="button" onClick={() => { setProperty(h.id, h.slug!); setSlug(h.slug!); }}
                        aria-label={`Book Now: ${h.title}`} data-testid={`booking-lightbox-choose-${h.slug}`}
                        className="inline-flex min-h-11 items-center justify-center rounded-lg border border-ink px-4 font-sans text-sm text-ink hover:bg-sand">Book Now</button>
                    </div>
                  </ClickableCard>
                  <LastMinuteOffer slug={h.slug} variant="badge" className="mt-2" onPick={(start, end) => { setProperty(h.id, h.slug!); setCheckIn(start); setCheckOut(end); setSlug(h.slug!); }} />
                </li>
              );
            })}
            {listed.length === 0 && <li className="font-sans text-luxury-sm text-ink/70">Loading homes…</li>}
          </ul>
        ) : (
          <div className="space-y-4 p-5">
            <div className="flex items-center gap-4">
              {coverOf(home) && <img src={img(coverOf(home)!, 240)} alt="" className="h-16 w-20 shrink-0 rounded-lg object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="font-serif text-xl text-ink">{home.title}</p>
                <Link href={`/properties/${home.slug}`} onClick={() => setOpen(false)} className="inline-flex items-center gap-1 font-sans text-[13px] text-forest underline-offset-4 hover:underline" data-testid="booking-lightbox-details">
                  View full details <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
            <BookingWithOffers
              key={`${home.slug}-${calendarKey}`}
              propertyId={home.id}
              propertySlug={home.slug!}
              autoOpenDates
              hydrateFromUrl={false}
            />
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
